import './Gauge.css';

import { type FunctionComponent, useMemo } from 'react';

import { useDimensions } from '../../hooks/useDimensions.hook.ts';

interface GaugeProps {
  deciles: [
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  value: number;
}

export const Gauge: FunctionComponent<GaugeProps> = ({ deciles, value }) => {
  const resolvedValue: number =
    value <= deciles[0] ? deciles[0] : value >= deciles[8] ? deciles[8] : value;

  const previousDecileIndex = deciles.findIndex((decile, index) => {
    if (index === deciles.length - 1) return resolvedValue >= decile;
    return resolvedValue >= decile && resolvedValue < (deciles[index + 1] ?? 0);
  });

  const actualIndex = previousDecileIndex === -1 ? 0 : previousDecileIndex;

  const [ref, { width }] = useDimensions<HTMLDivElement>();

  const triangleLeft = useMemo(() => {
    if (width === 0) return 0;

    if (resolvedValue >= deciles[8] || actualIndex === deciles.length - 1) {
      return width - 6;
    }

    const segmentStart = deciles[actualIndex] ?? 0;
    const segmentEnd = deciles[actualIndex + 1] ?? 0;
    const segmentProgress =
      (resolvedValue - segmentStart) / (segmentEnd - segmentStart);

    const segmentWidth = width / 9;
    const basePosition = actualIndex * segmentWidth;
    const positionInSegment = segmentProgress * segmentWidth;

    return Math.min(basePosition + positionInSegment, width - 6);
  }, [resolvedValue, deciles, actualIndex, width]);

  return (
    <div className="gauge-container">
      <div className="triangle-container">
        <div className="triangle" style={{ left: triangleLeft }} />
      </div>
      <div className="gauge" ref={ref} />
    </div>
  );
};
